import { useEnergyConsumptionCsvDownloader } from '@/features/energyxplorer/hooks/useNodeConsumptionCsvDownload';
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper'; // Adjust the import path as necessary
import { useTheme } from '@emotion/react';
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import FunctionsRoundedIcon from '@mui/icons-material/FunctionsRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import RssFeedRoundedIcon from '@mui/icons-material/RssFeedRounded';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const InfoNodeTab = ({ clickedNode, setValue }) => {
  const hasUnits = clickedNode?.dataStreams?.length > 0;
  const isResidual = clickedNode?.options !== null;
  const theme = useTheme();
  const { t } = useTranslation('energyxplorer-tree-tab', 'common');

  const { downloadEnergyCsv } = useEnergyConsumptionCsvDownloader();
  return (
    <Stack spacing={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center">
          <ListItem disablePadding disableGutters>
            <ListItemText
              primary={
                isResidual ? (
                  <>
                    {clickedNode?.name}{' '}
                    <Chip
                      size="small"
                      variant="filled"
                      icon={<AllInclusiveRoundedIcon fontSize="small" />}
                      label={'Residual Node'}
                    />
                  </>
                ) : clickedNode?.options === null &&
                  clickedNode?.dataStreams?.length > 0 ? (
                  <>
                    {clickedNode?.name}{' '}
                    <Chip
                      size="small"
                      variant="filled"
                      icon={<RssFeedRoundedIcon fontSize="small" />}
                      label={'Data Node'}
                    />
                  </>
                ) : clickedNode?.options === null &&
                  clickedNode?.dataStreams?.length === 0 ? (
                  <>
                    {' '}
                    {clickedNode?.name}{' '}
                    <Chip
                      size="small"
                      variant="filled"
                      icon={<FunctionsRoundedIcon fontSize="small" />}
                      label={'Virtuel Node'}
                    />
                  </>
                ) : (
                  clickedNode?.name
                )
              }
              secondary={
                <Typography variant="body2" color="textSecondary">
                  {hasUnits &&
                    `Baseret på datakilder fra ${clickedNode?.dataStreams?.length} enhed(er).`}
                  {isResidual &&
                    clickedNode?.options?.specialNodeType ===
                      'PercentageResidual' &&
                    ` Denne node estimerer forbruget baseret på ${clickedNode?.options?.percentage * 100}% af de øvrige noder i gruppen.`}
                  {isResidual &&
                    clickedNode?.options?.specialNodeType === 'FlatResidual' &&
                    ` Denne node har en fast anslået effekt på ${clickedNode?.options?.power} kW.`}

                  {isResidual &&
                    clickedNode?.options?.specialNodeType === 'RestResidual' &&
                    ` Denne node repræsenterer den resterende del af forbruget, som ikke er dækket af de andre noder i gruppen.`}
                </Typography>
              }
            />
          </ListItem>
        </Box>

        <Box
          sx={{
            backgroundColor: getTypeColor(clickedNode?.type),
            borderRadius: '25px',
            width: '40px',
            height: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: `1px solid divider`,
          }}
        >
          <i
            className={`fa-solid ${getTypeIcon(clickedNode?.type)}`}
            style={{ color: 'white' }}
          ></i>
        </Box>
      </Box>

      <Box display="flex" justifyContent="flex-start">
        <Box display="flex" flexDirection="column" flexGrow={1}>
          {hasUnits && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {clickedNode?.dataStreams?.map((ds) => (
                <Chip key={ds.unitId} label={ds.unitId} />
              ))}
            </Stack>
          )}

          {clickedNode?.dataStreams?.length === 0 && !isResidual && (
            <Alert
              severity="info"
              sx={(theme) => ({
                mt: 1,
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                borderLeft: `6px solid ${theme.palette.info.main}`,
              })}
            >
              Denne node er en{' '}
              <b>
                <u>virtuel node</u>
              </b>
              , hvilket betyder at den ikke har nogen direkte datakilder
              tilknyttet. Den kan bruges til at strukturere og organisere andre
              noder og deres data i træstrukturen.
            </Alert>
          )}

          {isResidual && (
            <Alert
              severity="info"
              sx={(theme) => ({
                mt: 1,
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                borderLeft: `6px solid ${theme.palette.info.main}`,
              })}
            >
              {/* <AlertTitle>Note</AlertTitle> */}
              {clickedNode?.options?.specialNodeType === 'FlatResidual' && (
                <>
                  Denne node er en{' '}
                  <u>
                    <b>flad residual node</b>
                  </u>
                  , hvilket betyder at den har en fast anslået effekt. Det er
                  ikke muligt at redigere, flytte eller tilføje noder under
                  denne type node.
                </>
              )}

              {clickedNode?.options?.specialNodeType ===
                'PercentageResidual' && (
                <>
                  Denne node er en{' '}
                  <u>
                    <b>procentuel residual node</b>
                  </u>
                  , hvilket betyder at den har en anslået effekt baseret på en
                  procentdel af de øvrige noder i gruppen. Det er ikke muligt at
                  redigere, flytte eller tilføje noder under denne type node.
                </>
              )}

              {clickedNode?.options?.specialNodeType === 'RestResidual' && (
                <>
                  Denne node er en{' '}
                  <u>
                    <b>rest residual node</b>
                  </u>
                  , hvilket betyder at den repræsenterer den resterende del af
                  forbruget, som ikke er dækket af de andre noder i gruppen. Det
                  er ikke muligt at redigere, flytte eller tilføje noder under
                  denne type node.
                </>
              )}
            </Alert>
          )}

          <Box display="flex" mt={1} gap={1}>
            <Button
              variant="text"
              endIcon={
                <KeyboardArrowRightRoundedIcon
                  sx={{ transition: 'margin-left 0.1s ease-in-out' }}
                />
              }
              onClick={() => setValue(2)}
              sx={{
                '&:hover': {
                  color: theme.palette.text.primary,
                  '& .MuiSvgIcon-root': { ml: 0.5 },
                  transition: 'margin-left 0.3s ease-in-out',
                },
              }}
            >
              {t(
                'node_dialog.info_node_tab.actions.open_in_consumption_analysis',
              )}
            </Button>
            <Button
              variant="text"
              endIcon={
                <KeyboardArrowRightRoundedIcon
                  sx={{ transition: 'margin-left 0.1s ease-in-out' }}
                />
              }
              onClick={() => downloadEnergyCsv(clickedNode)}
              sx={{
                '&:hover': {
                  color: theme.palette.text.primary,
                  '& .MuiSvgIcon-root': { ml: 0.5 },
                  transition: 'margin-left 0.3s ease-in-out',
                },
              }}
            >
              {t('node_dialog.info_node_tab.actions.download_csv')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};

export default InfoNodeTab;
